import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, Search, RefreshCw } from 'lucide-react'
import { listKostenuebernahmen, rematchAllUnmatched } from '@/api/kostenuebernahme'
import type { KostenuebernahmeStatus, MatchConfidence } from '@/types/kostenuebernahme'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'
import { useDebounce } from '@/hooks/useDebounce'

// ── Tab-Definitionen ─────────────────────────────────────────
type TabKey = KostenuebernahmeStatus | 'all'

const TABS: { key: TabKey; label: string; status?: KostenuebernahmeStatus }[] = [
  { key: 'pending_review', label: 'Inbox', status: 'pending_review' },
  { key: 'sent', label: 'Versendet', status: 'sent' },
  { key: 'in_progress', label: 'In Bearbeitung', status: 'in_progress' },
  { key: 'signed', label: 'Signiert', status: 'signed' },
  { key: 'cancelled', label: 'Storniert', status: 'cancelled' },
  { key: 'all', label: 'Alle' },
]

// ── Status-Badge ─────────────────────────────────────────────
const STATUS_COLORS: Record<KostenuebernahmeStatus, string> = {
  pending_review: 'bg-amber-100 text-amber-700',
  sent: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  signed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-500',
  pending_admin_approval: 'bg-purple-100 text-purple-700',
  admin_rejected_takeover: 'bg-orange-100 text-orange-700',
  admin_rejected_no_coverage: 'bg-rose-100 text-rose-700',
  unmatched_studi: 'bg-pink-100 text-pink-700',
}

const STATUS_LABELS: Record<KostenuebernahmeStatus, string> = {
  pending_review: 'Prüfen',
  sent: 'Versendet',
  in_progress: 'In Bearbeitung',
  signed: 'Signiert',
  cancelled: 'Storniert',
  pending_admin_approval: 'Admin-Prüfung',
  admin_rejected_takeover: 'Admin übernimmt',
  admin_rejected_no_coverage: 'Abgelehnt',
  unmatched_studi: 'Studi-Konflikt',
}

function StatusBadge({ status }: { status: KostenuebernahmeStatus }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_COLORS[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  )
}

// ── Match-Badge ───────────────────────────────────────────────
const CONFIDENCE_COLORS: Record<MatchConfidence, string> = {
  high: 'bg-green-100 text-green-700',
  low: 'bg-yellow-100 text-yellow-700',
  none: 'bg-red-100 text-red-600',
}

const CONFIDENCE_LABELS: Record<MatchConfidence, string> = {
  high: 'Sicher',
  low: 'Unsicher',
  none: 'Kein Match',
}

function MatchBadge({ confidence }: { confidence: MatchConfidence }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${CONFIDENCE_COLORS[confidence]}`}>
      {CONFIDENCE_LABELS[confidence]}
    </span>
  )
}

// ── Main Page ─────────────────────────────────────────────────
const PAGE_SIZE = 50

export default function KostenuebernahmenListPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabKey>('pending_review')
  const [search, setSearch] = useState('')
  const [rematching, setRematching] = useState(false)
  const [limit, setLimit] = useState<number>(PAGE_SIZE)
  const debouncedSearch = useDebounce(search, 300)

  const activeTabDef = TABS.find((t) => t.key === activeTab)!

  // Beim Tab- oder Such-Wechsel wieder auf erste Seite
  const handleSetActiveTab = useCallback((k: TabKey) => {
    setActiveTab(k)
    setLimit(PAGE_SIZE)
  }, [])

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['kostenuebernahmen', activeTab, debouncedSearch, limit],
    queryFn: () =>
      listKostenuebernahmen({
        status: activeTabDef.status,
        q: debouncedSearch || undefined,
        skip: 0,
        limit,
      }),
  })

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    setLimit(PAGE_SIZE)
  }, [])

  const handleRematchAll = useCallback(async () => {
    if (rematching) return
    if (!window.confirm('Die letzten 100 ungemachten Einträge im Hintergrund neu matchen?'))
      return
    setRematching(true)
    try {
      const r = await rematchAllUnmatched(100)
      window.alert(r.message)
      // Liste in 60s automatisch neu laden — bis dahin sollte der Sweep durch sein
      setTimeout(() => qc.invalidateQueries({ queryKey: ['kostenuebernahmen'] }), 60_000)
    } catch (e) {
      window.alert(`Fehler: ${(e as Error).message}`)
    } finally {
      setRematching(false)
    }
  }, [rematching, qc])

  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg px-6 py-5 mb-5 flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-[1.35rem] font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <FileText size={20} className="text-fom-500" />
            Zuzahler-Erklarungen
          </h1>
          <p className="text-gray-500 text-[0.78rem] mt-0.5">
            Kostenuebernahme-Vorgange verwalten
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRematchAll}
            disabled={rematching}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-fom-200 text-fom-700 bg-fom-50 hover:bg-fom-100 disabled:opacity-60 disabled:cursor-not-allowed"
            title="Die letzten 100 ungemachten Einträge gegen aktuelles CRM neu matchen"
          >
            <RefreshCw size={13} className={rematching ? 'animate-spin' : ''} />
            {rematching ? 'Matche…' : 'Letzte 100 neu matchen'}
          </button>

          {/* Suchfeld */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Name, Matrikel, Firma..."
              className="pl-8 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fom-500 outline-none w-64"
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-5 overflow-x-auto" aria-label="Status-Filter">
        {TABS.map((tab) => {
          const active = activeTab === tab.key
          return (
            <button
              key={tab.key}
              onClick={() => handleSetActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-[2px] whitespace-nowrap transition-colors ${
                active
                  ? 'border-fom-500 text-fom-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Content */}
      {isLoading && <Spinner className="mt-10" />}
      {error && <ErrorAlert message={(error as Error).message} onRetry={refetch} />}

      {!isLoading && !error && (
        <>
          <div className="text-xs text-gray-400 mb-2">
            {items.length < total
              ? `Zeige ${items.length} von ${total} Einträgen`
              : `${total} Einträge`}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Studierende/r</th>
                  <th className="px-4 py-3 text-left">Matrikel</th>
                  <th className="px-4 py-3 text-left">Zuzahler-Firma</th>
                  <th className="px-4 py-3 text-left">AP-Kontakt</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Match</th>
                  <th className="px-4 py-3 text-left">Erstellt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr
                    key={item.Id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/superadmin/kostenuebernahmen/${item.Id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {[item.StudiAnrede, item.StudiVorname, item.StudiNachname]
                        .filter(Boolean)
                        .join(' ') || '–'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                      {item.StudiMatrikel ?? '–'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      <div className="font-medium">{item.ZuzahlerFirma ?? '–'}</div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {(item.ZuzahlerApName || item.ZuzahlerApEmail) ? (
                        <div className="space-y-0.5">
                          {item.ZuzahlerApName && (
                            <div className="text-sm font-medium text-gray-800">{item.ZuzahlerApName}</div>
                          )}
                          {item.ZuzahlerApEmail && (
                            <a
                              href={`mailto:${item.ZuzahlerApEmail}?subject=${encodeURIComponent(
                                `Kostenübernahme ${[item.StudiVorname, item.StudiNachname].filter(Boolean).join(' ')}`
                              )}`}
                              className="text-xs text-fom-600 hover:text-fom-700 hover:underline break-all"
                            >
                              {item.ZuzahlerApEmail}
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs">–</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={item.Status} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <MatchBadge confidence={item.MatchConfidence} />
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {new Date(item.CreatedAt).toLocaleDateString('de-DE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {items.length === 0 && (
              <div className="text-center text-gray-400 py-12 text-sm">
                Keine Einträge vorhanden
              </div>
            )}
          </div>

          {items.length < total && (
            <div className="flex justify-center mt-4">
              <button
                onClick={() => setLimit((l) => l + PAGE_SIZE)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
              >
                Weitere {Math.min(PAGE_SIZE, total - items.length)} laden
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
